<template>
  <div class="text-[13px] secondary_color proxima_nova font-normal">
    <SkeletonLoader
      v-if="loadingEntity || loadingFormLayout || props.loadingSchema"
      :page="loaderPages.FORM_INPUT"
    />
    <a-form
      v-else
      ref="formRef"
      :model="formState"
      layout="vertical"
      :rules="formRules"
      @finish="onFinish"
      @finishFailed="onFinishFailed"
      :required-mark="false"
    >
      <div class="grid grid-cols-1">
        <div
          class="mb-5"
          v-for="property in filteredProperties"
          :key="property.slug || property.name"
        >
          <a-form-item
            :name="getFieldKey(property)"
            :rules="getFormFieldRules(property)"
            :help="getFieldHelp(property)"
            :validateStatus="
              getDuplicateRecord(property)
                ? 'error'
                : getFieldValidateStatus(property)
            "
            class="mb-0"
          >
            <template #label>
              <span class="secondary_color text-[13px] inline-flex items-center gap-1"
                >{{ getFieldLabel(property)
                }}<span v-if="property.is_required" class="secondary_color"
                  >*</span
                ><a-tooltip
                  v-if="fieldTooltips[property.slug]"
                  :title="fieldTooltips[property.slug]"
                  placement="top"
                >
                  <ThemeImage
                    :icon="imageNames.INFO_GREY"
                    class="size-3 cursor-help opacity-70"
                    alt="info"
                  />
                </a-tooltip>
              </span>
            </template>
            <vue-tel-input
              v-if="isPhoneInput(property)"
              v-model="formState[getFieldKey(property)]"
              :id="`form_item_${getFieldKey(property)}`"
              :defaultCountry="defaultCountryCode"
              :dropdownOptions="{
                showFlags: true,
                showDialCodeInList: true,
                showSearchBox: true,
                searchBoxPlaceholder: 'Search country..',
              }"
              mode="international"
              :class="{
                'phone-error-input': phoneFieldErrors[getFieldKey(property)],
              }"
              @update:modelValue="onPhoneModelUpdate(property, $event)"
              @blur="
                validatePhoneField(property);
                checkDuplicateOnBlur(property);
              "
            />

            <!-- File type field -->
            <CrmFileUploadField
              v-else-if="isFileInput(property)"
              v-model="formState[getFieldKey(property)]"
              :property="property"
              :object-slug="slug"
              :record-id="entityId"
              :is-standard-object="false"
              @files-changed="handleFilesChanged(property, $event)"
            />

            <!-- Record type field -->
            <div
              v-else-if="isRecordInput(property)"
              :style="getCustomFieldErrorStyle(property)"
            >
              <RecordTypeSelector
                v-model="formState[getFieldKey(property)]"
                :related-objects="property.related_objects || []"
                :attribute="property"
                :single="isSingleSelectRelationField(slug, property.slug)"
                :create-options="getRecordCreateOptions(property)"
                :placeholder="`Select ${getFieldLabel(property)}`"
              />
            </div>

            <!-- Current Company field for contacts/leads -->
            <div
              v-else-if="isCurrentCompanyField(property)"
              :style="getCustomFieldErrorStyle(property)"
            >
              <CompanySelect
                :form-state="formState"
                :selected-company-id="selectedCompanyId"
                :initialSelectedCompanies="initialSelectedCompanies"
                @update:selected-company-id="handleCompanyIdChange"
                @update:selected-company="handleCompanyObjectChange"
                :contact-id="isContacts ? entityId : undefined"
                :lead-id="isLeads ? entityId : undefined"
                :label="getFieldLabel(property)"
                :field-name="getFieldKey(property)"
                :placeholder="`Select ${getFieldLabel(property)}`"
                title-classes="secondary_color text-[13px] hidden"
              />
            </div>

            <!-- Pipeline field for deals -->
            <a-select
              v-else-if="isPipelineField(property)"
              v-model:value="formState.pipeline_id"
              show-search
              :filter-option="
                (input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
              "
              :placeholder="`Select ${getFieldLabel(property)}`"
              @change="updatePipelineStages"
              :class="{
                'w-full': true,
                'error-border': fieldErrors[getFieldKey(property)],
              }"
            >
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_arrow"
                  class="size-4"
                />
              </template>
              <a-select-option
                v-for="pipeline in pipelines"
                :key="pipeline.id"
                :value="pipeline.id"
                :label="pipeline.name"
              >
                {{ pipeline.name }}
              </a-select-option>
            </a-select>

            <!-- Pipeline Stage field for deals -->
            <a-select
              v-else-if="isPipelineStageField(property)"
              v-model:value="formState.pipeline_stage_id"
              show-search
              :filter-option="
                (input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
              "
              :placeholder="`Select ${getFieldLabel(property)}`"
              :class="{
                'w-full': true,
                'error-border': fieldErrors[getFieldKey(property)],
              }"
            >
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_arrow"
                  class="size-4"
                />
              </template>
              <a-select-option
                v-for="stage in pipelineStages"
                :key="stage.id"
                :value="stage.id"
                :label="stage.name"
              >
                {{ stage.name }}
              </a-select-option>
            </a-select>

            <!-- Amount with Currency field for deals -->
            <div
              v-else-if="isAmountField(property)"
              :style="getCustomFieldErrorStyle(property)"
            >
              <CustomDropdownInput
                :dropdownOptions="currencyDropdownOptions"
                :selectedOption="selectedCurrency"
                @update:selectedOption="handleCurrencyChange"
                :inputValue="formState[getFieldKey(property)]"
                @update:inputValue="formState[getFieldKey(property)] = $event"
                :showSymbol="true"
                placeholder="0.00"
                numeric-only
              />
            </div>

            <!-- Schema currency field ({ currency_value, currency_code } payload) -->
            <div
              v-else-if="isSchemaCurrencyField(property)"
              :style="getCustomFieldErrorStyle(property)"
            >
              <CustomDropdownInput
                :dropdownOptions="currencyDropdownOptions"
                :selectedOption="getCurrencyFieldCode(property)"
                @update:selectedOption="updateCurrencyFieldCode(property, $event)"
                :inputValue="getCurrencyFieldValue(property)"
                @update:inputValue="updateCurrencyFieldValue(property, $event)"
                :showSymbol="true"
                placeholder="0.00"
                numeric-only
                :disabled="disabledFields.includes(property.slug)"
              />
            </div>

            <!-- List IDs field for contacts/leads/companies -->
            <div
              v-else-if="isListIdsField(property)"
              :style="getCustomFieldErrorStyle(property)"
            >
              <ListsSelect
                v-model="formState.list_ids"
                :entityType="getEntityTypeForLists()"
                title-classes="secondary_color text-[13px] hidden"
              />
            </div>

            <!-- Parent dropdown for custom objects -->
            <a-select
              v-else-if="isParentField(property)"
              v-model:value="formState.parent_id"
              :placeholder="`Select ${entityDisplayName}`"
              show-search
              :filter-option="false"
              class="w-full"
              allow-clear
              :loading="parentLoading"
              @search="handleParentSearch"
              @popupScroll="handleParentPopupScroll"
            >
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_arrow"
                  class="size-4"
                />
              </template>
              <template v-if="parentLoading" #notFoundContent>
                <div class="flex items-center justify-center py-4">
                  <a-spin
                    size="small"
                    class="primary_color [&_.ant-spin-dot-item]:bg-[var(--foreground-rgb)]"
                  />
                </div>
              </template>
              <a-select-option
                v-for="option in parentObjectOptions"
                :key="option.value"
                :value="option.value"
              >
                {{ option.label }}
              </a-select-option>
              <a-select-option
                v-if="parentLoadingMore"
                key="__loading_more__"
                disabled
              >
                <div class="flex items-center justify-center py-2">
                  <a-spin
                    size="small"
                    class="primary_color [&_.ant-spin-dot-item]:bg-[var(--foreground-rgb)]"
                  />
                </div>
              </a-select-option>
            </a-select>

            <!-- Tags field for standard entities -->
            <a-select
              v-else-if="isTagsField(property)"
              v-model:value="formState[getFieldKey(property)]"
              mode="tags"
              :placeholder="`Select ${getFieldLabel(property)}`"
              :class="{
                'w-full': true,
                'error-border': fieldErrors[getFieldKey(property)],
              }"
              show-search
              :filter-option="
                (input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
              "
              :options="getFieldOptions(property)"
              :token-separators="[',']"
              :loading="loadingFields[getFieldKey(property)] || false"
            >
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_arrow"
                  class="size-4"
                />
              </template>
            </a-select>

            <!-- Owner field for standard entities -->
            <a-select
              v-else-if="isOwnerField(property)"
              v-model:value="formState[getFieldKey(property)]"
              :placeholder="`Select ${getFieldLabel(property)}`"
              :class="{
                'w-full': true,
                'error-border': fieldErrors[getFieldKey(property)],
              }"
              show-search
              :filter-option="
                (input, option) =>
                  option.label.toLowerCase().includes(input.toLowerCase())
              "
              :loading="usersLoading"
              allow-clear
              optionLabelProp="label"
            >
              <template #suffixIcon>
                <ThemeImage
                  :icon="imageNames.BOTTOM_ARROW"
                  alt="bottom_arrow"
                  class="size-4"
                />
              </template>
              <a-select-option
                v-for="option in getFieldOptions(property)"
                :key="option.value"
                :value="option.value"
                :label="option.label"
                :disabled="option.isDeleted"
              >
                <div class="flex items-center gap-2">
                  <RemovedUserIcon v-if="option.isDeleted" :size="24" />
                  <ProfileIcon
                    v-else
                    :data="option"
                    :view="true"
                    :param="'name'"
                    :size="24"
                    :font="10"
                  />
                  <RemovedUserLabel
                    v-if="option.isDeleted"
                    :name="option.label"
                    :is-deleted="true"
                  />
                  <span v-else>{{ option.label }}</span>
                </div>
              </a-select-option>
            </a-select>

            <template v-else>
              <component
                :is="getComponentConfig(property).component"
                v-model:value="formState[getFieldKey(property)]"
                v-bind="{
                  ...getComponentConfig(property).props,
                  placeholder: getComponentConfig(property).placeholder,
                  class: getComponentConfig(property).class,
                  'allow-clear': getComponentConfig(property).allowClear,
                  ...(getComponentConfig(property).options && {
                    options: getComponentConfig(property).options,
                  }),
                  ...(getDuplicateRecord(property) && { status: 'error' }),
                }"
                :class="getFieldErrorClass(property)"
                :loading="loadingFields[getFieldKey(property)] || false"
                :onSearch="
                  shouldUseApiSearch(property) &&
                  getComponentConfig(property).suffixIcon
                    ? (q) => handleFieldSearch(property, q)
                    : undefined
                "
                @blur="checkDuplicateOnBlur(property)"
                @change="handleFieldChange(property)"
                @dropdownVisibleChange="
                  (visible) => handleDropdownVisibleChange(property, visible)
                "
              >
                <template
                  v-if="getComponentConfig(property).suffixIcon"
                  #suffixIcon
                >
                  <ThemeImage
                    :icon="imageNames.BOTTOM_ARROW"
                    alt="bottom_arrow"
                    class="size-4"
                  />
                </template>
                <template
                  v-if="
                    getComponentConfig(property).suffixIcon &&
                    loadingFields[getFieldKey(property)]
                  "
                  #notFoundContent
                >
                  <div class="flex items-center justify-center py-4">
                    <a-spin
                      size="small"
                      class="primary_color [&_.ant-spin-dot-item]:bg-[var(--foreground-rgb)]"
                    />
                  </div>
                </template>
              </component>
            </template>
          </a-form-item>

          <!-- Duplicate record warning and link -->
          <div v-if="getDuplicateRecord(property)" class="mt-2">
            <span class="text-xs text-red-500 font-normal">
              A record with this
              {{ getFieldLabel(property).toLowerCase() }} already exists
            </span>
            <ExistingEntity
              class="mt-2"
              :existing-entity="getDuplicateRecord(property)"
              :object-slug="slug"
              :primary-attribute-slug="primaryAttributeSlug"
            />
          </div>
        </div>
      </div>

      <!-- Deal Lost Reason field - only shown when deal stage is Lost -->
      <div v-if="isDeals && isLostStage" class="mb-5">
        <a-form-item name="deal_lost_reason" class="mb-0">
          <template #label>
            <span class="secondary_color text-[13px]">Deal Lost Reason</span>
          </template>
          <a-textarea
            v-model:value="formState.deal_lost_reason"
            class="w-full"
            :rows="4"
            placeholder="Enter the reason why this deal was lost"
          />
        </a-form-item>
      </div>

      <div
        v-if="hasNoProperties"
        class="flex flex-col items-center justify-center py-12"
      >
        <EmptyScreen
          source="add-new-entity-form"
          text="No properties available"
          css="size-[72px]"
        >
          <a-button class="primary_small mt-4" @click="handleCreateProperty">
            Create property
          </a-button>
        </EmptyScreen>
      </div>
    </a-form>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { message } from "ant-design-vue";
import dayjs from "dayjs";
import debounce from "lodash/debounce.js";
import { VueTelInput } from "vue-tel-input";
import ThemeImage from "@/src/components/ThemeImage.vue";
import ProfileIcon from "@/src/components/ProfileIcon.vue";
import RemovedUserIcon from "@/src/components/RemovedUserIcon.vue";
import RemovedUserLabel from "@/src/components/RemovedUserLabel.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import ExistingEntity from "@/src/components/ExistingEntity.vue";
import CrmFileUploadField from "@/src/components/crm_file_components/CrmFileUploadField.vue";
import RecordTypeSelector from "@/src/components/record_type_components/RecordTypeSelector.vue";
import CompanySelect from "@/src/components/CompanySelect.vue";
import { PAYMENT_TERM_FIELD_TOOLTIPS } from "@/src/views/invoices/config/invoiceFormConfig.js";
import ListsSelect from "@/src/components/ListsSelect.vue";
import CustomDropdownInput from "@/src/components/custom_components/CustomDropdownInput.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import { dealStages } from "@/src/constants/dealConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { useEntityTypeDetection } from "@/src/composables/useEntityTypeDetection.js";
import {
  SPECIAL_FIELD_TYPES,
  getSpecialFieldType,
} from "@/src/composables/useFieldRendering.js";

const store = useStore();
const defaultCountryCode = computed(
  () => store.getters["workspace/defaultCountryCode"],
);
const currentUserId = computed(
  () => store.getters["auth/userData"]?.id || null,
);

import {
  ATTRIBUTE_TYPES,
  SHARED_CURRENCY_CODE_SLUGS,
  getEntityTypeFromSlug,
  STANDARD_OBJECT_SLUGS,
  AUTO_SELECT_ASSOCIATIONS_SLUGS,
  isSingleSelectRelationField,
  getServiceForSlug,
  shouldHideParentFieldForSlug,
  shouldExcludeFormField,
  excludedCreateOnlyFields,
} from "@/src/constants/crmConstants.js";
import {
  normalizeApiAttributes,
  fetchFieldOptions,
  getUnifiedPlaceholder,
  getOptimizedComponentConfig,
  isPhoneInput,
  isDateInput,
  isFileInput,
  isRecordInput,
  getFieldKey,
  getFieldLabel,
  needsFieldOptions,
  getFieldOptionsFromProperty,
  getFieldRules,
  getFieldDefaultValue,
  normalizeArrayFieldValue,
  normalizeOriginalDataForTransform,
  normalizeTimestamp,
  shouldUseApiSearch,
  fetchEntityLabelsForIds,
  fetchDealRelationships,
  filterStagesByPipeline,
  toFormOptions,
  isFieldVisibleInForm,
} from "@/src/utils/dynamicFormUtils.js";
import { getRelatedObject } from "@/src/utils/relationshipUtils.js";
import { isValidPhone } from "@/src/utils/phoneNumberUtils.js";
import { extractPhoneNumber } from "@/src/utils/dynamicCRMViewUtils.js";
import { AbortControllerMap } from "@/src/utils/debouncedSearch.js";
import { getPrimaryAttributeSlug } from "@/src/utils/entityUtils.js";
import { useSelectInfiniteScroll } from "@/src/composables/useSelectInfiniteScroll.js";
import CRMService from "@/src/api/CRMService.js";
import TicketsService from "@/src/api/TicketsService.js";
import DashboardService from "@/src/api/DashboardService.js";

const props = defineProps({
  properties: { type: Array, default: () => [] },
  relationships: { type: Array, default: () => [] },
  subObjects: { type: Array, default: () => [] },
  slug: { type: String, required: true },
  visible: { type: Boolean, required: true },
  initialData: { type: Object, default: () => ({}) },
  entityId: { type: [String, Number], default: null },
  entityDisplayName: { type: String, default: "" },
  bulkCreateMode: { type: Boolean, default: false },
  loadingSchema: { type: Boolean, default: false },
  hideRelationships: { type: Boolean, default: false },
  // Map of attribute-slug → tooltip text. Renders an info icon next to the
  // field label with this text on hover. Use for explaining business-specific
  // fields the schema doesn't carry help text for (e.g. payment-term discount).
  fieldTooltips: {
    type: Object,
    default: () => ({}),
  },
  hideParentField: { type: Boolean, default: false },
  disabledFields: { type: Array, default: () => [] },
  service: { type: String, default: null },
});

const emit = defineEmits([
  "form-ready",
  "form-submit",
  "properties-empty",
  "duplicate-found",
  "form-changed",
]);

const router = useRouter();
const formRef = ref();
const formState = reactive({});
const loadingFields = ref({});
const fieldOptions = ref({});
const phoneFieldErrors = ref({});
const fieldErrors = ref({});
const loadingEntity = ref(false);
const isEditMode = computed(() => !!props.entityId);

const {
  isTickets,
  isTasks,
  isContacts,
  isLeads,
  isCompanies,
  isDeals,
  isStandardEntity,
  isContactOrLead,
  isCRMTasks,
} = useEntityTypeDetection(() => props.slug);

// Lists is a special UI-only field (not part of the backend schema). We inject
// it as a synthetic property so it can be reordered/hidden via the customise
// form layout, while keeping it scoped to the create/edit form only.
const supportsListIds = computed(
  () => isContacts.value || isLeads.value || isCompanies.value,
);

const buildSyntheticListIdsProperty = () => ({
  id: "list_ids",
  slug: "list_ids",
  name: "Lists",
  type: "multi_select",
  datatype: "multi_select",
  is_required: false,
  is_editable: true,
  is_internal: false,
  _isVirtualListIds: true,
});

const supportsParentField = computed(() => {
  if (props.hideParentField) return false;
  if (isTickets.value || isTasks.value) return false;
  if (isStandardEntity.value) return false;
  const resolvedService =
    props.service || getServiceForSlug(props.slug, store);
  if (shouldHideParentFieldForSlug(props.slug, resolvedService)) return false;
  if (props.slug === entityTypes.APPROVALS) return false;
  return true;
});

const buildSyntheticParentProperty = () => ({
  id: "parent_id",
  slug: "parent_id",
  name: `Parent ${props.entityDisplayName || ""}`.trim() || "Parent",
  type: "select",
  datatype: "select",
  is_required: false,
  is_editable: true,
  is_internal: false,
  _isVirtualParent: true,
});

const originalRecord = ref(null);
const formLayoutAttributes = ref([]);
const formLayoutRelationships = ref([]);
const loadingFormLayout = ref(false);
const schemaAttributes = ref([]);
const schemaRelationships = ref([]);
const primaryAttributeSlug = ref("");

const duplicateRecords = ref({});
const checkingDuplicates = ref({});

const pipelines = ref([]);
const pipelineStages = ref([]);
const workspaceCurrencies = ref([]);
const selectedCurrency = ref("USD");
const selectedCompanyId = ref(null);
const initialSelectedCompanies = ref([]);
const tagOptions = ref([]);
const users = ref([]);
const usersLoading = ref(false);

// Resolve once: caller-provided prop wins, else slug → service lookup.
// Every data-fetch call in this form should use this so same-slug objects
// across services (e.g. CRM products vs products-service products) hit
// the right backend.
const effectiveService = computed(
  () => props.service || getServiceForSlug(props.slug, store),
);

const getViewIdForSlug = async (slug) => {
  try {
    const viewData = await CRMService.fetchDynamicEntityViews(
      slug,
      null,
      getServiceForSlug(slug, store),
    );
    return viewData?.success && viewData?.currentView?.id
      ? viewData.currentView.id
      : 1;
  } catch (error) {
    console.error("Error fetching view ID:", error);
    return 1;
  }
};

const buildParentObjectOptions = (records = []) => {
  return records
    .filter((record) => {
      if (isEditMode.value && props.entityId) {
        return record?.id !== props.entityId;
      }
      return true;
    })
    .map((record) => {
      const attributes = record?.attributes || {};
      let label = null;

      if (primaryAttributeSlug.value) {
        const primaryValue = attributes[primaryAttributeSlug.value]?.value;
        if (primaryValue && String(primaryValue).trim()) {
          label = String(primaryValue);
        }
      }

      if (!label) {
        for (const key in attributes) {
          const value = attributes[key]?.value;
          if (value && String(value).trim()) {
            label = String(value);
            break;
          }
        }
      }

      if (!label) {
        label = record?.id || "Untitled";
      }

      return {
        value: record.id,
        label: label,
      };
    });
};

// Cached viewId to avoid re-fetching on every page load
let cachedParentViewId = null;

const parentFetchFn = async (page, pageSize, searchQuery) => {
  const hasSearchQuery = searchQuery && searchQuery.trim() !== "";
  let response;

  if (hasSearchQuery) {
    response = await CRMService.searchDynamicObjects({
      object_type: props.slug,
      search_query: searchQuery,
      page,
      page_size: pageSize,
      service: effectiveService.value,
    });
  } else {
    if (!cachedParentViewId) {
      cachedParentViewId = await getViewIdForSlug(props.slug);
    }
    response = await CRMService.fetchDynamicEntityData(
      props.slug,
      cachedParentViewId,
      page,
      pageSize,
      undefined,
      null,
      {},
      null,
      null,
      effectiveService.value,
    );
  }

  const success = response?.success;
  const results = success ? response.results || [] : [];
  const total = success ? response.total || 0 : 0;
  return { items: results, total };
};

const {
  items: parentRawItems,
  loading: parentLoading,
  loadingMore: parentLoadingMore,
  handlePopupScroll: handleParentPopupScroll,
  handleSearch: handleParentSearch,
  fetchFirstPage: fetchParentFirstPage,
  reset: resetParentScroll,
} = useSelectInfiniteScroll({
  fetchFn: parentFetchFn,
  pageSize: 20,
  debounceMs: 300,
});

const parentObjectOptions = computed(() =>
  buildParentObjectOptions(parentRawItems.value),
);

const isCurrentCompanyField = (property) => {
  if (!isContactOrLead.value) return false;
  return getSpecialFieldType(property) === SPECIAL_FIELD_TYPES.CURRENT_COMPANY;
};

const isPipelineField = (property) => {
  if (!isDeals.value) return false;
  return getSpecialFieldType(property) === SPECIAL_FIELD_TYPES.PIPELINE;
};

const isPipelineStageField = (property) => {
  if (!isDeals.value) return false;
  return getSpecialFieldType(property) === SPECIAL_FIELD_TYPES.PIPELINE_STAGE;
};

const isDealTypeField = (property) => {
  if (!isDeals.value) return false;
  return getSpecialFieldType(property) === SPECIAL_FIELD_TYPES.DEAL_TYPE;
};

const isPriorityField = (property) => {
  if (!isDeals.value) return false;
  return getSpecialFieldType(property) === SPECIAL_FIELD_TYPES.PRIORITY;
};

const isAmountField = (property) => {
  if (!isDeals.value) return false;
  return getSpecialFieldType(property) === SPECIAL_FIELD_TYPES.AMOUNT;
};

// Schema-driven currency attribute (any object's attribute with type === "currency").
// The deals "amount" field is handled separately via isAmountField.
// Value is stored as a compound object { currency_value, currency_code } so it submits in that shape.
const isSchemaCurrencyField = (property) => {
  if (!property || isAmountField(property)) return false;
  const type = (property.type || property.datatype || "").toLowerCase();
  return type === ATTRIBUTE_TYPES.CURRENCY;
};

const hasSchemaCurrencyField = computed(() =>
  (props.properties || []).some((p) => isSchemaCurrencyField(p)),
);

// For slugs like "prd_products", all currency attributes share one currency code;
// changing the code on any field updates all of them on the record.
const sharesCurrencyCode = computed(() =>
  SHARED_CURRENCY_CODE_SLUGS.includes(props.slug),
);

const primaryCurrencyCode = computed(() => {
  const primary = workspaceCurrencies.value.find((c) => c.is_primary);
  return (
    primary?.currency_code ||
    workspaceCurrencies.value[0]?.currency_code ||
    "USD"
  );
});

const getCurrencyFieldValue = (property) => {
  const key = getFieldKey(property);
  const val = formState[key];
  return val && typeof val === "object" ? val.currency_value : val;
};

const getCurrencyFieldCode = (property) => {
  const key = getFieldKey(property);
  const val = formState[key];
  if (val && typeof val === "object" && val.currency_code) return val.currency_code;
  return primaryCurrencyCode.value;
};

const updateCurrencyFieldValue = (property, newValue) => {
  const key = getFieldKey(property);
  const current = formState[key];
  const code =
    (current && typeof current === "object" && current.currency_code) ||
    primaryCurrencyCode.value;
  formState[key] = { currency_value: newValue, currency_code: code };
};

const updateCurrencyFieldCode = (property, newCode) => {
  const writeCode = (prop) => {
    const key = getFieldKey(prop);
    const current = formState[key];
    const value =
      current && typeof current === "object" ? current.currency_value : current;
    formState[key] = { currency_value: value ?? "", currency_code: newCode };
  };

  if (sharesCurrencyCode.value) {
    (props.properties || [])
      .filter((p) => isSchemaCurrencyField(p))
      .forEach(writeCode);
  } else {
    writeCode(property);
  }
};

const isListIdsField = (property) => {
  if (!isContacts.value && !isLeads.value && !isCompanies.value) return false;
  return getSpecialFieldType(property) === SPECIAL_FIELD_TYPES.LIST_IDS;
};

const isParentField = (property) => {
  if (!supportsParentField.value) return false;
  return (
    !!property?._isVirtualParent ||
    property?.id === "parent_id" ||
    property?.slug === "parent_id"
  );
};

const isTagsField = (property) => {
  if (!isStandardEntity.value) return false;
  const fieldType = (property.type || property.datatype || "").toLowerCase();
  return (
    getSpecialFieldType(property) === SPECIAL_FIELD_TYPES.TAGS ||
    fieldType === "tags"
  );
};

const isOwnerField = (property) => {
  if (!isStandardEntity.value) return false;
  const fieldType = getSpecialFieldType(property);
  return (
    fieldType === SPECIAL_FIELD_TYPES.OWNER ||
    fieldType === SPECIAL_FIELD_TYPES.DEAL_OWNER
  );
};

const isLostStage = computed(() => {
  if (!isDeals.value) return false;
  // Find the actual slug for pipeline_stage_id (handles renamed fields)
  // Check both slug and id for robustness
  const stageAttr = (props.properties || []).find(
    (p) => p.id === "pipeline_stage_id",
  );
  const stageSlug = stageAttr?.slug || "pipeline_stage_id";
  const stageValue = formState[stageSlug] ?? formState.pipeline_stage_id;
  const selectedStage = pipelineStages.value.find(
    (stage) => stage.id === stageValue,
  );
  if (!selectedStage) return false;
  return (
    selectedStage.probability === "LOST" ||
    selectedStage.probability === "lost" ||
    selectedStage?.name?.toLowerCase() === dealStages?.LOST?.toLowerCase()
  );
});

const currencyDropdownOptions = computed(() => {
  if (workspaceCurrencies.value.length > 0) {
    return workspaceCurrencies.value.map((curr) => ({
      label: `${curr.currency_name} (${curr.currency_code}) ${curr.currency_symbol}`,
      labelToShow: curr.currency_code,
      value: curr.currency_code,
      symbol: curr.currency_symbol,
    }));
  }
  return [];
});

// Helper to get pipeline field slugs (handles renamed fields)
const getPipelineFieldSlugs = () => {
  const pipelineAttr = (props.properties || []).find(
    (p) => p.id === "pipeline_id",
  );
  const stageAttr = (props.properties || []).find(
    (p) => p.id === "pipeline_stage_id",
  );
  return {
    pipelineSlug: pipelineAttr?.slug || "pipeline_id",
    stageSlug: stageAttr?.slug || "pipeline_stage_id",
  };
};

const initializePipelineOptions = () => {
  if (!isDeals.value) return;
  const pipelineAttr = (props.properties || []).find(
    (p) => p.id === "pipeline_id",
  );
  pipelines.value = (pipelineAttr?.structured_options || []).map((opt) => ({
    id: Number(opt.id) || opt.id,
    name: opt.label,
  }));
  const { pipelineSlug } = getPipelineFieldSlugs();
  // Check formState first, then fallback to props.initialData (handles timing issues)
  const existingValue =
    formState[pipelineSlug] ??
    formState.pipeline_id ??
    props.initialData?.[pipelineSlug] ??
    props.initialData?.pipeline_id;
  if (existingValue) {
    // Ensure formState has the value (may come from initialData)
    formState[pipelineSlug] = existingValue;
  } else if (pipelines.value.length > 0) {
    formState[pipelineSlug] = pipelines.value[0]?.id;
  }
  updatePipelineStages();
};

const updatePipelineStages = () => {
  const { pipelineSlug, stageSlug } = getPipelineFieldSlugs();
  const pipelineAttr = (props.properties || []).find(
    (p) => p.id === "pipeline_id",
  );
  const stageAttr = (props.properties || []).find(
    (p) => p.id === "pipeline_stage_id",
  );
  const selectedPipelineId = formState[pipelineSlug] ?? formState.pipeline_id;
  const allStages = stageAttr?.structured_options || [];
  const filtered = filterStagesByPipeline(
    allStages,
    selectedPipelineId,
    pipelineAttr?.structured_options || [],
  );
  pipelineStages.value = filtered.map((s) => ({
    id: Number(s.id) || s.id,
    name: s.label.replace(/\s*\([^)]+\)$/, ""),
  }));
  const currentStage = formState[stageSlug] ?? formState.pipeline_stage_id;
  if (
    !pipelineStages.value.some((s) => String(s.id) === String(currentStage))
  ) {
    formState[stageSlug] = pipelineStages.value[0]?.id;
  }
};

// Helper to get currency field slug (handles renamed fields)
const getCurrencyFieldSlug = () => {
  const currencyAttr = (props.properties || []).find(
    (p) => p.id === "currency_code",
  );
  return currencyAttr?.slug || "currency_code";
};

const fetchWorkspaceCurrencies = async () => {
  if (!isDeals.value && !hasSchemaCurrencyField.value) return;
  try {
    const response = await DashboardService.getWorkspaceCurrencies();
    if (response?.success && response?.currencies?.length > 0) {
      workspaceCurrencies.value = response.currencies;
      if (isDeals.value) {
        const currencySlug = getCurrencyFieldSlug();
        // Check both slug and id for existing value
        const existingValue = formState[currencySlug] ?? formState.currency_code;
        if (!existingValue) {
          const primaryCurrency = workspaceCurrencies.value.find(
            (curr) => curr.is_primary,
          );
          const defaultCurrency =
            primaryCurrency || workspaceCurrencies.value[0];
          if (defaultCurrency) {
            selectedCurrency.value = defaultCurrency.currency_code;
            formState[currencySlug] = defaultCurrency.currency_code;
          }
        } else {
          selectedCurrency.value = existingValue;
        }
      }
    }
  } catch (error) {
    console.error("Error fetching workspace currencies:", error);
  }
};

const handleCurrencyChange = (currencyCode) => {
  selectedCurrency.value = currencyCode;
  const currencySlug = getCurrencyFieldSlug();
  formState[currencySlug] = currencyCode;
};

const fetchTagsForEntity = async () => {
  if (!isStandardEntity.value) return;

  const tagsAttr = (props.properties || []).find((p) => p.id === "tags");
  const tagsSlug = tagsAttr?.slug || "tags";

  // Use schema options if available instead of making an API call
  if (tagsAttr?.options?.length > 0) {
    const options = toFormOptions(tagsAttr.options);
    tagOptions.value = options;
    fieldOptions.value.tags = options;
    fieldOptions.value[tagsSlug] = options;
    return;
  }

  try {
    let response;
    if (isContacts.value) {
      response = await DashboardService.getTagsForContact();
    } else if (isLeads.value) {
      response = await DashboardService.getTagsForLead();
    } else if (isCompanies.value) {
      response = await DashboardService.getTagsForCompany();
    } else if (isDeals.value) {
      response = await DashboardService.getTagsForDeal();
    }
    if (response?.success && response?.tags) {
      tagOptions.value = response.tags;
      // Store options by both ID and slug for compatibility
      fieldOptions.value.tags = response.tags;
      fieldOptions.value[tagsSlug] = response.tags;
    }
  } catch (error) {
    console.error("Error fetching tags:", error);
  }
};

const fetchEntityLists = async () => {
  if (!isEditMode.value || !props.entityId) return;
  if (!isContacts.value && !isLeads.value && !isCompanies.value) return;

  try {
    const entityType = getEntityTypeFromSlug(props.slug);
    const response = await DashboardService.getListsByEntityId(
      entityType,
      props.entityId,
    );
    if (response?.success && Array.isArray(response.lists)) {
      const listIds = response.lists.map((list) => list.id);
      formState.list_ids = listIds;
    }
  } catch (error) {
    console.error("Error fetching entity lists:", error);
    if (!Array.isArray(formState.list_ids)) {
      formState.list_ids = [];
    }
  }
};

const fetchUsers = async () => {
  if (!isStandardEntity.value) return;
  usersLoading.value = true;
  try {
    // Use usersWithDeleted to include deleted users (shown as disabled in dropdown)
    let storeUsers = store.getters["users/usersWithDeleted"];
    if (!storeUsers || storeUsers.length === 0) {
      await store.dispatch("users/fetchUsersWithDeleted");
      storeUsers = store.getters["users/usersWithDeleted"];
    }

    if (storeUsers && storeUsers.length > 0) {
      users.value = storeUsers;
      // Find the actual slug for owner field (handles renamed fields)
      const ownerFieldId = isDeals.value ? "deal_owner_id" : "owner_id";
      const ownerAttr = (props.properties || []).find(
        (p) => p.id === ownerFieldId,
      );
      const ownerFieldSlug = ownerAttr?.slug || ownerFieldId;

      // Check both slug and id for existing value
      const existingValue =
        formState[ownerFieldSlug] ?? formState[ownerFieldId];
      if (!existingValue && !isEditMode.value) {
        const currentUserEmail = store.getters["auth/userData"]?.email;
        const currentUser = storeUsers.find(
          (u) => u.email === currentUserEmail && !u.isDeleted,
        );
        if (currentUser) {
          formState[ownerFieldSlug] = currentUser.id;
        }
      }
      const freshUsers = store.getters["users/users"] || [];
      const userOptions = storeUsers
        .filter((user) => !user.isDeleted || isEditMode.value)
        .map((user) => {
          const freshUser = freshUsers.find((u) => u.id === user.id);
          return {
            id: user.id,
            value: user.id,
            label: user.name || `${user.first_name} ${user.last_name}`.trim(),
            name: user.name || `${user.first_name} ${user.last_name}`.trim(),
            profile_pic: user.profile_pic,
            avatar_color: user.avatar_color || freshUser?.avatar_color,
            ...(user.isDeleted ? { isDeleted: true, disabled: true } : {}),
          };
        });
      // Store options by both ID and slug for compatibility
      fieldOptions.value[ownerFieldId] = userOptions;
      fieldOptions.value[ownerFieldSlug] = userOptions;
    }
  } catch (error) {
    console.error("Error fetching users:", error);
  } finally {
    usersLoading.value = false;
  }
};

const handleCompanyIdChange = (value) => {
  if (Array.isArray(value)) {
    selectedCompanyId.value = value[0] || null;
  } else {
    selectedCompanyId.value = value;
  }
};

const handleCompanyObjectChange = (companyObject) => {
  // Set the full company object for the API (not just the ID)
  formState.current_company = companyObject;
};

const getEntityTypeForLists = () => {
  if (isContacts.value) return "Contact";
  if (isLeads.value) return "Lead";
  if (isCompanies.value) return "Company";
  return null;
};

const hasOptionValue = (property, value) => {
  const options = property?.options || property?.structured_options || [];
  if (options.length === 0) return false;
  return options.some((opt) => {
    if (typeof opt === "string") return opt === value;
    return opt?.value === value || opt?.label === value;
  });
};

const formatSlugAsDisplayName = (slug) => {
  if (!slug) return "";
  if (slug.toLowerCase() === "crm_tasks" || slug.toLowerCase() === "crm_task") {
    return "CRM Task" + (slug.toLowerCase().endsWith("s") ? "s" : "");
  }
  return slug
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const getRelationshipDisplayName = (relationship, currentSlug) => {
  const relatedInfo = getRelatedObject(relationship, currentSlug);

  if (!relatedInfo) return "Associated Unknown";

  let objectName =
    relatedInfo.object?.name ||
    relatedInfo.object?.plural_name ||
    relatedInfo.object?.slug ||
    relatedInfo.slug;

  if (!objectName && relatedInfo.objectId) {
    objectName = relatedInfo.objectId;
  }



  const formattedName = formatSlugAsDisplayName(objectName || "Unknown");
  return `Associated ${formattedName}`;
};

const relationshipProperties = computed(() => {
  if (props.hideRelationships) return [];

  // Don't show relationships for contacts, leads, companies as their POST APIs don't support it
  if (isContacts.value || isLeads.value || isCompanies.value) {
    return [];
  }

  const layoutRels = formLayoutRelationships.value || [];

  let effectiveRelationships =
    props.relationships?.length > 0
      ? props.relationships
      : schemaRelationships.value || [];

  const isStandardEntitySlug = STANDARD_OBJECT_SLUGS.includes(
    props.slug?.toLowerCase(),
  );

  if (isStandardEntitySlug) {
    effectiveRelationships = effectiveRelationships.filter((rel) => {
      const relatedInfo = getRelatedObject(rel, props.slug);
      const relatedSlug = relatedInfo?.slug?.toLowerCase() || "";
      return STANDARD_OBJECT_SLUGS.includes(relatedSlug);
    });
  }

  const schemaRelMap = effectiveRelationships.reduce((acc, rel) => {
    if (rel && rel.id) acc[rel.id] = rel;
    return acc;
  }, {});

  const layoutAttrs = Array.isArray(formLayoutAttributes.value)
    ? formLayoutAttributes.value
    : [];

  // Only built-in entities (deals, crm_tasks) auto-show all schema
  // relationships when no layout exists. User-created custom objects must
  // opt in via the customise form, matching how the configurator behaves.
  const autoShowsRelationships = AUTO_SELECT_ASSOCIATIONS_SLUGS.includes(
    (props.slug || "").toLowerCase(),
  );

  if (autoShowsRelationships && layoutRels.length === 0) {
    const result = effectiveRelationships.map((schemaRel, index) => {
      const relatedInfo = getRelatedObject(schemaRel, props.slug);
      const relatedSlug = relatedInfo?.slug || "";
      const displayName = getRelationshipDisplayName(schemaRel, props.slug);

      return {
        id: schemaRel.id,
        slug: relatedSlug,
        name: displayName,
        label: displayName,
        type: "relationship",
        datatype: "relationship",
        is_required: false,
        is_editable: true,
        ordering: 1000 + index, // High ordering to appear after all form attributes
        metadata: {
          source_object: schemaRel.source_object,
          target_object: schemaRel.target_object,
          source_object_id: schemaRel.source_object_id,
          target_object_id: schemaRel.target_object_id,
          cardinality: schemaRel.cardinality,
        },
        _isVirtualRelationship: true,
        _relationshipId: schemaRel.id,
      };
    });
    return result;
  }

  return layoutRels
    .filter((layoutRel) => {
      if (!layoutRel.relationship_id) return false;
      if (layoutRel.is_visible === false) return false;
      return !!schemaRelMap[layoutRel.relationship_id];
    })
    .map((layoutRel) => {
      const schemaRel = schemaRelMap[layoutRel.relationship_id];
      const relatedInfo = getRelatedObject(schemaRel, props.slug);
      const relatedSlug = relatedInfo?.slug || "";
      const displayName = getRelationshipDisplayName(schemaRel, props.slug);

      return {
        id: schemaRel.id,
        slug: relatedSlug,
        name: displayName,
        label: displayName,
        type: "relationship",
        datatype: "relationship",
        is_required: layoutRel.is_required ?? false,
        is_editable: true,
        ordering: layoutRel.ordering ?? Number.POSITIVE_INFINITY,
        metadata: {
          source_object: schemaRel.source_object,
          target_object: schemaRel.target_object,
          source_object_id: schemaRel.source_object_id,
          target_object_id: schemaRel.target_object_id,
          cardinality: schemaRel.cardinality,
        },
        _isVirtualRelationship: true,
        _relationshipId: schemaRel.id,
      };
    });
});

const filteredProperties = computed(() => {
  const baseList = (props.properties || []).filter((property) => {
    const fieldName = getFieldKey(property);
    const propertyType = (
      property.type ||
      property.datatype ||
      ""
    ).toLowerCase();
    if (shouldExcludeFormField(fieldName, propertyType, property.id))
      return false;
    if (!isEditMode.value && property.id) {
      const isExcludedInCreateMode = excludedCreateOnlyFields.some(
        (excluded) => excluded.toLowerCase() === property.id.toLowerCase(),
      );
      if (isExcludedInCreateMode) return false;
    }
    if (property.is_editable === false) return false;
    if (
      propertyType === ATTRIBUTE_TYPES.FORMULA ||
      propertyType === ATTRIBUTE_TYPES.PROPERTY_SYNC
    )
      return false;
    if (property.is_internal === true) return false;
    if (props.bulkCreateMode && propertyType === "relationship") return false;
    return true;
  });

  const layout = Array.isArray(formLayoutAttributes.value)
    ? formLayoutAttributes.value
    : [];
  const relLayout = Array.isArray(formLayoutRelationships.value)
    ? formLayoutRelationships.value
    : [];

  if (layout.length === 0 && relLayout.length === 0) {
    const relationshipsToAdd = props.bulkCreateMode
      ? []
      : relationshipProperties.value;
    const result = [...baseList, ...relationshipsToAdd];
    // No layout at all → behave like every other field: include Lists / parent
    // by default. Once a layout exists, the post-layout-filter branch below
    // strictly respects whatever it says about list_ids / parent_id.
    if (
      supportsParentField.value &&
      !result.some((p) => p.id === "parent_id" || p.slug === "parent_id")
    ) {
      result.unshift(buildSyntheticParentProperty());
    }
    if (
      supportsListIds.value &&
      !result.some((p) => p.id === "list_ids" || p.slug === "list_ids")
    ) {
      result.push(buildSyntheticListIdsProperty());
    }
    return result;
  }

  const idToConfig = layout.reduce((acc, item) => {
    if (item && item.attribute_id) acc[item.attribute_id] = item;
    return acc;
  }, {});

  const relIdToConfig = relLayout.reduce((acc, item) => {
    if (item && item.relationship_id) acc[item.relationship_id] = item;
    return acc;
  }, {});

  const slugToId = (schemaAttributes.value || []).reduce((acc, attr) => {
    if (attr && attr.slug && attr.id) acc[attr.slug] = attr.id;
    return acc;
  }, {});

  const filteredByLayout = baseList.filter((prop) => {
    if (prop.is_required === true) return true;

    const attributeId = prop.id || slugToId[prop.slug];
    const cfg = attributeId ? idToConfig[attributeId] : undefined;
    if (!cfg) return false;
    if (cfg.is_visible === false) return false;
    return true;
  });

  // Inject the synthetic Lists field ONLY when the layout has an explicit
  // list_ids entry with is_visible !== false. Missing entry => hide.
  if (
    supportsListIds.value &&
    !filteredByLayout.some((p) => p.id === "list_ids" || p.slug === "list_ids")
  ) {
    const listCfg = idToConfig["list_ids"];
    if (listCfg && listCfg.is_visible !== false) {
      filteredByLayout.push(buildSyntheticListIdsProperty());
    }
  }

  // Same rule for the Parent dropdown on custom objects.
  if (
    supportsParentField.value &&
    !filteredByLayout.some(
      (p) => p.id === "parent_id" || p.slug === "parent_id",
    )
  ) {
    const parentCfg = idToConfig["parent_id"];
    if (parentCfg && parentCfg.is_visible !== false) {
      filteredByLayout.push(buildSyntheticParentProperty());
    }
  }

  const relationshipsToAdd = props.bulkCreateMode
    ? []
    : relationshipProperties.value;
  const allProperties = [...filteredByLayout, ...relationshipsToAdd];

  allProperties.sort((a, b) => {
    let oa, ob;

    if (a._isVirtualRelationship) {
      oa = a.ordering;
    } else {
      const aId = a.id || slugToId[a.slug];
      oa =
        aId && idToConfig[aId]?.ordering !== undefined
          ? idToConfig[aId].ordering
          : Number.POSITIVE_INFINITY;
    }

    if (b._isVirtualRelationship) {
      ob = b.ordering;
    } else {
      const bId = b.id || slugToId[b.slug];
      ob =
        bId && idToConfig[bId]?.ordering !== undefined
          ? idToConfig[bId].ordering
          : Number.POSITIVE_INFINITY;
    }

    return oa - ob;
  });

  const result = allProperties.map((prop) => {
    if (prop._isVirtualRelationship) {
      return prop; // Already has is_required set
    }
    const attributeId = prop.id || slugToId[prop.slug];
    const cfg = (attributeId && idToConfig[attributeId]) || {};
    return { ...prop, is_required: cfg.is_required ?? prop.is_required };
  });

  return result;
});

const hasNoProperties = computed(() => {
  // Synthetic UI-only fields (parent_id, list_ids) don't count — the empty
  // state should still fire when there are no real schema attributes.
  return filteredProperties.value.every(
    (p) => p._isVirtualParent || p._isVirtualListIds,
  );
});

const uniqueProperties = computed(() => {
  return filteredProperties.value.filter(
    (property) => property.is_unique === true,
  );
});

const isUniqueProperty = (property) => {
  return property.is_unique === true;
};

const hasDuplicates = computed(() => {
  return Object.values(duplicateRecords.value).some(
    (record) => record !== null,
  );
});

const getDuplicateRecord = (property) => {
  const fieldKey = getFieldKey(property);
  return duplicateRecords.value[fieldKey] || null;
};

const checkDuplicateOnBlur = async (property) => {
  if (!isUniqueProperty(property)) return;

  const fieldKey = getFieldKey(property);
  const value = formState[fieldKey];

  if (value === undefined || value === null || String(value).trim() === "") {
    duplicateRecords.value[fieldKey] = null;
    emit("duplicate-found", {
      hasDuplicate: hasDuplicates.value,
      duplicates: duplicateRecords.value,
    });
    return;
  }

  checkingDuplicates.value[fieldKey] = true;

  try {
    const result = await CRMService.checkDuplicateRecord(
      props.slug,
      property.slug,
      value,
      isEditMode.value ? props.entityId : null,
      effectiveService.value,
    );

    if (result?.success && result?.exists && result?.record) {
      duplicateRecords.value[fieldKey] = result.record;
    } else {
      duplicateRecords.value[fieldKey] = null;
    }

    emit("duplicate-found", {
      hasDuplicate: hasDuplicates.value,
      duplicates: duplicateRecords.value,
    });
  } catch (error) {
    console.error(`Error checking duplicate for ${fieldKey}:`, error);
    duplicateRecords.value[fieldKey] = null;
  } finally {
    checkingDuplicates.value[fieldKey] = false;
  }
};

const clearDuplicateOnChange = (property) => {
  if (!isUniqueProperty(property)) return;

  const fieldKey = getFieldKey(property);
  if (duplicateRecords.value[fieldKey]) {
    duplicateRecords.value[fieldKey] = null;
    emit("duplicate-found", {
      hasDuplicate: hasDuplicates.value,
      duplicates: duplicateRecords.value,
    });
  }
};

const clearFieldError = (property) => {
  const key = getFieldKey(property);
  if (fieldErrors.value[key]) {
    delete fieldErrors.value[key];
  }
};

const handleFieldChange = (property) => {
  clearDuplicateOnChange(property);
  clearFieldError(property);
};

const handleFilesChanged = (property, files) => {
  const fieldKey = getFieldKey(property);
  formState[fieldKey] = files;
};

const getFieldPlaceholder = (property) => getUnifiedPlaceholder(property);

const getComponentConfig = (property) => {
  const config = getOptimizedComponentConfig(property, {
    class: "w-full",
    currentSlug: props.slug,
  });

  if (config.needsOptions) {
    config.options = getFieldOptions(property);
  }

  return config;
};

const getFieldOptions = (property) => {
  const fieldKey = getFieldKey(property);
  return getFieldOptionsFromProperty(property, fieldOptions.value);
};

const handleCreateProperty = () => {
  router.push(`/settings/objects/customize/${props.slug}?tab=properties`);
};

const fetchFormLayout = async () => {
  loadingFormLayout.value = true;
  try {
    if (isTickets.value) {
      formLayoutAttributes.value = [];
      formLayoutRelationships.value = [];
      try {
        const schemaRes = await TicketsService.getTicketSchema();
        const schemaAttrs = Array.isArray(schemaRes?.schema?.attributes)
          ? schemaRes.schema.attributes
          : [];
        schemaAttributes.value = schemaAttrs;
        schemaRelationships.value = [];
      } catch (e) {
        console.warn("Failed to load schema attributes for tickets", e);
        schemaAttributes.value = [];
        schemaRelationships.value = [];
      }
    } else {
      // Prefer prop, fall back to slug lookup.
      const effectiveService =
        props.service || getServiceForSlug(props.slug, store);
      const response = await CRMService.getForm(props.slug, effectiveService);
      const allFields = Array.isArray(response?.attributes)
        ? response.attributes
        : [];

      formLayoutAttributes.value = allFields.filter(
        (field) => field.attribute_id && field.attribute_id.trim() !== "",
      );
      formLayoutRelationships.value = allFields.filter(
        (field) => field.relationship_id && field.relationship_id.trim() !== "",
      );

      try {
        const schemaRes = await CRMService.fetchSchemaObject(
          props.slug,
          effectiveService,
        );
        const schemaAttrs = Array.isArray(schemaRes?.schema?.attributes)
          ? schemaRes.schema.attributes
          : [];
        schemaAttributes.value = schemaAttrs;
        schemaRelationships.value = Array.isArray(
          schemaRes?.schema?.relationships,
        )
          ? schemaRes.schema.relationships
          : [];
      } catch (e) {
        console.warn("Failed to load schema attributes for id mapping", e);
        schemaAttributes.value = [];
        schemaRelationships.value = [];
      }
    }
  } catch (error) {
    console.error("Error fetching form layout:", error);
    formLayoutAttributes.value = [];
    formLayoutRelationships.value = [];
  } finally {
    loadingFormLayout.value = false;
  }
};

const getFormFieldRules = (property) => {
  const baseRules = getFieldRules(property);

  if (isPhoneInput(property)) {
    baseRules.push({
      validator: (_, value) => {
        if (value === undefined || value === null || value === "") {
          return Promise.resolve();
        }
        return isValidPhone(String(value))
          ? Promise.resolve()
          : Promise.reject(new Error("Please enter a valid phone number"));
      },
      trigger: ["change", "blur"],
    });
  }

  return baseRules;
};

const getCustomFieldErrorStyle = (property) => {
  const key = getFieldKey(property);
  return fieldErrors.value[key]
    ? {
        border: "1px solid var(--error-color)",
        borderRadius: "8px",
        padding: "2px",
      }
    : {};
};

const getFieldHelp = (property) => {
  if (!isPhoneInput(property)) return undefined;
  const key = getFieldKey(property);
  const msg = phoneFieldErrors.value[key];
  return msg || undefined;
};

const getFieldValidateStatus = (property) => {
  if (!isPhoneInput(property)) return undefined;
  const key = getFieldKey(property);
  return phoneFieldErrors.value[key] ? "error" : undefined;
};

const getFieldErrorClass = (property) => {
  const key = getFieldKey(property);
  if (!fieldErrors.value[key]) return {};
  const component = getComponentConfig(property).component;
  if (component === "a-select") return { "error-border": true };
  if (component === "a-date-picker") return { "error-border": true };
  if (component === "a-input-number") return { "has-error": true };
  return {};
};

const formRules = computed(() => {
  const rules = {};
  (filteredProperties.value || []).forEach((property) => {
    const fieldKey = getFieldKey(property);
    rules[fieldKey] = getFormFieldRules(property);
  });
  return rules;
});

const fetchDynamicOptions = async (property) => {
  const fieldKey = getFieldKey(property);

  if (!needsFieldOptions(property)) return;

  loadingFields.value[fieldKey] = true;

  try {
    const options = await fetchFieldOptions(property, {
      entitySlug: props.slug,
      fieldKey: fieldKey,
    });

    fieldOptions.value[fieldKey] = options;
  } catch (error) {
    console.error(`Error fetching options for field ${fieldKey}:`, error);
    message.error(`Failed to load options for ${getFieldLabel(property)}`);
  } finally {
    loadingFields.value[fieldKey] = false;
  }
};

const abortManager = new AbortControllerMap();

const handleFieldSearchInternal = async (property, searchQuery) => {
  const fieldKey = getFieldKey(property);

  if (!needsFieldOptions(property)) {
    return;
  }

  const controller = abortManager.create(fieldKey);

  try {
    const options = await fetchFieldOptions(property, {
      entitySlug: props.slug,
      fieldKey: fieldKey,
      searchQuery: searchQuery,
      signal: controller.signal,
    });

    if (!AbortControllerMap.isAborted(controller.signal)) {
      fieldOptions.value[fieldKey] = options;
    }
  } catch (error) {
    if (!AbortControllerMap.isAbortError(error)) {
      console.error(`Error searching for field ${fieldKey}:`, error);
    }
  } finally {
    if (!AbortControllerMap.isAborted(controller.signal)) {
      loadingFields.value[fieldKey] = false;
      abortManager.cleanup(fieldKey);
    }
  }
};

const debouncedFieldSearchInternal = debounce(handleFieldSearchInternal, 300);

const handleFieldSearch = (property, searchQuery) => {
  const fieldKey = getFieldKey(property);

  if (!shouldUseApiSearch(property)) {
    return;
  }

  loadingFields.value[fieldKey] = true;

  fieldOptions.value[fieldKey] = [];

  debouncedFieldSearchInternal(property, searchQuery);
};

const handleDropdownVisibleChange = (property, visible) => {
  if (visible && shouldUseApiSearch(property)) {
    fetchDynamicOptions(property);
  }
};

const fetchEntityData = async () => {
  if (!isEditMode.value || !props.entityId) return null;

  loadingEntity.value = true;
  try {
    let response;
    if (isTickets.value) {
      response = await TicketsService.getTicketByID(props.entityId);
    } else {
      response = await CRMService.fetchDynamicEntityDetails(
        props.slug,
        props.entityId,
        effectiveService.value,
      );
    }

    if (response?.success) {
      const record = isTickets.value ? response.ticket : response.record;
      if (record) {
        return record;
      } else {
        message.error("Failed to load entity details");
        return null;
      }
    } else {
      message.error("Failed to load entity details");
      return null;
    }
  } catch (error) {
    console.error("Error fetching entity details:", error);
    message.error("Failed to load entity details");
    return null;
  } finally {
    loadingEntity.value = false;
  }
};

const initializeForm = async () => {
  let dataToUse = props.initialData || {};

  if (isEditMode.value) {
    const freshData = await fetchEntityData();
    if (freshData) {
      dataToUse = freshData;
    }
  }

  originalRecord.value = dataToUse || {};

  const normalized = { ...dataToUse };
  if (normalized.attributes && typeof normalized.attributes === "object") {
    Object.assign(
      normalized,
      normalizeApiAttributes(normalized.attributes, props.properties),
    );
  }
  if (normalized.relations && typeof normalized.relations === "object") {
    Object.keys(normalized.relations).forEach((relKey) => {
      const ids = Array.isArray(normalized.relations[relKey])
        ? normalized.relations[relKey]
        : [];
      normalized[relKey] = ids;
    });
  } else if (isDeals.value && isEditMode.value && props.entityId) {
    const dealRelations = await fetchDealRelationships(props.entityId);
    if (dealRelations.companies.length > 0) {
      normalized.companies = dealRelations.companies;
    }
    if (dealRelations.contacts.length > 0) {
      normalized.contacts = dealRelations.contacts;
    }
    if (dealRelations.leads.length > 0) {
      normalized.leads = dealRelations.leads;
    }
  }
  Object.assign(formState, normalized);

  // Map current_company from initial data to the actual field slug (handles renamed fields)
  // This is needed because AddContact/AddLead pass current_company in initialData,
  // but the field slug might have been changed (e.g., to 'orgw')
  const currentCompanyProperty = (props.properties || []).find(
    (prop) => prop.id === "current_company",
  );
  if (
    (isContacts.value || isLeads.value) &&
    formState.current_company &&
    currentCompanyProperty
  ) {
    const actualSlug = getFieldKey(currentCompanyProperty);
    if (actualSlug !== "current_company") {
      formState[actualSlug] = formState.current_company;
    }
  }

  const propertiesBySlug = (props.properties || []).reduce((acc, p) => {
    acc[p.slug] = p;
    return acc;
  }, {});

  (props.properties || []).forEach((property) => {
    const fieldKey = getFieldKey(property);
    if (isRecordInput(property) && !Array.isArray(formState[fieldKey])) {
      formState[fieldKey] = [];
    }
  });

  Object.keys(formState).forEach((key) => {
    const property = propertiesBySlug[key];
    if (property && Array.isArray(formState[key])) {
      const fieldType = (
        property.type ||
        property.datatype ||
        ""
      ).toLowerCase();

      if (fieldType === ATTRIBUTE_TYPES.PHONE_NUMBER) {
        formState[key] = extractPhoneNumber(formState?.[key]?.[0]);
      } else if (fieldType === ATTRIBUTE_TYPES.DOMAIN) {
        formState[key] = formState?.[key]?.[0]?.domain || "";
      } else {
        formState[key] = normalizeArrayFieldValue(formState[key], property);
      }
    }

    if (property) {
      const fieldType = (
        property.type ||
        property.datatype ||
        ""
      ).toLowerCase();

      if (
        fieldType === ATTRIBUTE_TYPES.DATETIME &&
        typeof formState[key] === "number"
      ) {
        const normalizedTs = normalizeTimestamp(formState[key]);
        if (normalizedTs !== null) {
          const d = dayjs(normalizedTs);
          if (d.isValid()) {
            formState[key] = d;
          }
        }
      }
    }
  });

  (filteredProperties.value || []).forEach((property) => {
    const fieldKey = getFieldKey(property);

    if (formState[fieldKey] === undefined) {
      formState[fieldKey] = getFieldDefaultValue(property);
    }

    const typeLower = String(
      property.type || property.datatype || "",
    ).toLowerCase();
    if (typeLower === "relationship") {
      const existing = formState[fieldKey];
      const existingArray = Array.isArray(existing) ? existing : [];

      // Check if values are raw IDs (not label-value objects)
      const hasRawIds =
        existingArray.length > 0 &&
        existingArray.some(
          (item) => typeof item === "string" || typeof item === "number",
        );

      if (hasRawIds && property._isVirtualRelationship) {
        const relatedSlug =
          property.slug || property.metadata?.target_object_id;

        if (relatedSlug) {
          fetchEntityLabelsForIds(relatedSlug, existingArray).then(
            (labelValueItems) => {
              formState[fieldKey] = labelValueItems;
            },
          );
        } else {
          formState[fieldKey] = existingArray.map((id) => ({
            label: String(id),
            value: id,
          }));
        }
      } else {
        formState[fieldKey] = existingArray;
      }
    }

    if (
      typeLower === "tags" ||
      fieldKey === "tags" ||
      typeLower === ATTRIBUTE_TYPES.MULTI_SELECT
    ) {
      const existing = formState[fieldKey];
      if (Array.isArray(existing)) {
        formState[fieldKey] = existing.filter(
          (v) => v !== null && v !== undefined && v !== "",
        );
      } else {
        formState[fieldKey] = [];
      }
    }

    if (isDateInput(property) && formState[fieldKey]) {
      const fieldType = (
        property.type ||
        property.datatype ||
        ""
      ).toLowerCase();

      if (
        fieldType === ATTRIBUTE_TYPES.DATETIME &&
        typeof formState[fieldKey] === "number"
      ) {
        const normalizedTs = normalizeTimestamp(formState[fieldKey]);
        if (normalizedTs !== null) {
          formState[fieldKey] = dayjs(normalizedTs);
        }
      } else if (
        fieldType === ATTRIBUTE_TYPES.DATE &&
        typeof formState[fieldKey] === "string"
      ) {
        if (!dayjs.isDayjs(formState[fieldKey])) {
          formState[fieldKey] = dayjs(formState[fieldKey]);
        }
      } else if (
        fieldType === ATTRIBUTE_TYPES.DATETIME &&
        typeof formState[fieldKey] === "string"
      ) {
        if (!dayjs.isDayjs(formState[fieldKey])) {
          formState[fieldKey] = dayjs(formState[fieldKey]);
        }
      } else if (!dayjs.isDayjs(formState[fieldKey])) {
        formState[fieldKey] = dayjs(formState[fieldKey]);
      }
    }
  });

  if (isCRMTasks.value && !isEditMode.value && currentUserId.value) {
    // Find the actual slug for assigned_to field (handles renamed fields)
    const assignedToAttr = (props.properties || []).find(
      (p) => p.id === "assigned_to",
    );
    const assignedToSlug = assignedToAttr?.slug || "assigned_to";
    // Check both slug and id for existing value
    const existingValue = formState[assignedToSlug] ?? formState.assigned_to;
    if (!existingValue) {
      formState[assignedToSlug] = currentUserId.value;
    }
  }

  (filteredProperties.value || []).forEach((property) => {
    const fieldKey = getFieldKey(property);

    if (isOwnerField(property) || isTagsField(property)) {
      return;
    }

    if (needsFieldOptions(property)) {
      loadingFields.value[fieldKey] = false;
      fetchDynamicOptions(property);
    }
  });

  await fetchPrimaryAttributeSlug();
  if (supportsParentField.value) {
    cachedParentViewId = null;
    resetParentScroll();
    fetchParentFirstPage();
  }

  if (isStandardEntity.value) {
    fetchUsers();

    fetchTagsForEntity();

    if (isContacts.value || isLeads.value || isCompanies.value) {
      if (isEditMode.value) {
        if (!Array.isArray(formState.list_ids)) {
          fetchEntityLists();
        }
      } else if (!Array.isArray(formState.list_ids)) {
        formState.list_ids = [];
      }
    }

    if (isDeals.value) {
      initializePipelineOptions();
      fetchWorkspaceCurrencies();
      if (!isEditMode.value) {
        // Find the actual slugs for deal_type and priority fields (handles renamed fields)
        const dealTypeAttr = (props.properties || []).find(
          (p) => p.id === "deal_type",
        );
        const priorityAttr = (props.properties || []).find(
          (p) => p.id === "priority",
        );
        const dealTypeSlug = dealTypeAttr?.slug || "deal_type";
        const prioritySlug = priorityAttr?.slug || "priority";

        if (!formState[dealTypeSlug] && hasOptionValue(dealTypeAttr, "New")) {
          formState[dealTypeSlug] = "New";
        }
        if (
          !formState[prioritySlug] &&
          hasOptionValue(priorityAttr, "Medium")
        ) {
          formState[prioritySlug] = "Medium";
        }
      }
    }

    if (isLeads.value) {
      if (!isEditMode.value) {
        // Find the actual slug for lead_status field (handles renamed fields)
        const leadStatusAttr = (props.properties || []).find(
          (p) => p.id === "lead_status",
        );
        const leadStatusSlug = leadStatusAttr?.slug || "lead_status";
        if (
          !formState[leadStatusSlug] &&
          hasOptionValue(leadStatusAttr, "New")
        ) {
          formState[leadStatusSlug] = "New";
        }
      }
    }

    if (isContacts.value) {
      if (!isEditMode.value) {
        // Find the actual slug for contact_status field (handles renamed fields)
        const contactStatusAttr = (props.properties || []).find(
          (p) => p.id === "contact_status",
        );
        const contactStatusSlug = contactStatusAttr?.slug || "contact_status";
        if (
          !formState[contactStatusSlug] &&
          hasOptionValue(contactStatusAttr, "New")
        ) {
          formState[contactStatusSlug] = "New";
        }
      }
    }

    if ((isContacts.value || isLeads.value) && isEditMode.value) {
      // Find the current company field by its constant ID (handles renamed fields)
      const currentCompanyProperty = (props.properties || []).find(
        (prop) => prop.id === "current_company",
      );
      const currentCompanySlug = currentCompanyProperty
        ? getFieldKey(currentCompanyProperty)
        : "current_company";
      const currentCompanyValue = formState[currentCompanySlug];

      // Also get current_company_id for constructing proper company object
      const currentCompanyIdProperty = (props.properties || []).find(
        (prop) => prop.id === "current_company_id",
      );
      const currentCompanyIdSlug = currentCompanyIdProperty
        ? getFieldKey(currentCompanyIdProperty)
        : "current_company_id";
      const currentCompanyId = formState[currentCompanyIdSlug];

      if (currentCompanyValue) {
        // Construct a proper company object with id for deduplication in CompanySelect
        if (typeof currentCompanyValue === "object" && currentCompanyValue.id) {
          // Already a proper object
          initialSelectedCompanies.value = [currentCompanyValue];
          selectedCompanyId.value = currentCompanyValue.id;
        } else if (currentCompanyId) {
          // currentCompanyValue is just the name string, construct proper object
          initialSelectedCompanies.value = [
            {
              id: currentCompanyId,
              name:
                typeof currentCompanyValue === "string"
                  ? currentCompanyValue
                  : currentCompanyValue?.name || "",
            },
          ];
          selectedCompanyId.value = currentCompanyId;
        } else {
          // Fallback: no id available
          initialSelectedCompanies.value = [currentCompanyValue];
        }
      }
    }
  }

  // Fires for non-deal objects (custom objects like products, invoices) that have
  // at least one attribute with type === "currency". Deals handle this above.
  if (!isDeals.value && hasSchemaCurrencyField.value) {
    fetchWorkspaceCurrencies();
  }
};

const fetchPrimaryAttributeSlug = async () => {
  try {
    const slug = await getPrimaryAttributeSlug(props.slug);
    primaryAttributeSlug.value = slug || "";
  } catch (error) {
    console.error("Error fetching primary attribute slug:", error);
    primaryAttributeSlug.value = "";
  }
};

const onFinish = (values) => {
  fieldErrors.value = {};
  const processedValues = { ...values };

  (filteredProperties.value || []).forEach((property) => {
    const fieldKey = getFieldKey(property);
    if (isPhoneInput(property)) {
      const raw = processedValues[fieldKey];
      if (raw === undefined || raw === null || String(raw).trim() === "") {
        phoneFieldErrors.value[fieldKey] = ""; // don't validate empties here
      } else {
        phoneFieldErrors.value[fieldKey] = !isValidPhone(String(raw))
          ? "Please enter a valid phone number"
          : "";
      }
    }
    if (isDateInput(property) && processedValues[fieldKey]) {
      if (dayjs.isDayjs(processedValues[fieldKey])) {
        const fieldType = (
          property.type ||
          property.datatype ||
          ""
        ).toLowerCase();
        processedValues[fieldKey] =
          fieldType === ATTRIBUTE_TYPES.DATETIME
            ? processedValues[fieldKey].format("YYYY-MM-DD HH:mm:ss")
            : processedValues[fieldKey].format("YYYY-MM-DD");
      }
    }
  });

  emit("form-submit", processedValues);
};

const scrollToFirstError = (errorInfo) => {
  try {
    const first = errorInfo?.errorFields?.[0];
    if (first?.name && formRef.value?.scrollToField) {
      formRef.value.scrollToField(first.name, {
        behavior: "smooth",
        block: "center",
      });
    }
  } catch (e) {}
};

const onFinishFailed = (errorInfo) => {
  scrollToFirstError(errorInfo);
  const errors = {};
  (errorInfo.errorFields || []).forEach(({ name }) => {
    const fieldKey = Array.isArray(name) ? name[0] : name;
    errors[fieldKey] = true;
  });
  fieldErrors.value = errors;
  message.error("Please fix the form errors before submitting");
};

const validatePhoneField = (property) => {
  const key = getFieldKey(property);
  const value = formState[key];
  if (value === undefined || value === null || String(value).trim() === "") {
    phoneFieldErrors.value[key] = "";
    // Also clear AntD internal validation when empty so the helper disappears immediately
    try {
      formRef.value?.clearValidate?.([key]);
    } catch (e) {}
    return true;
  }

  const valid = isValidPhone(String(value));
  const errorMessage = valid ? "" : "Please enter a valid phone number";
  phoneFieldErrors.value[key] = errorMessage;
  if (valid) {
    formRef.value?.clearValidate?.([key]);
  }

  return valid;
};

const onPhoneModelUpdate = (property, newValue) => {
  const key = getFieldKey(property);
  const hadError = !!phoneFieldErrors.value[key];
  const valid = isValidPhone(String(newValue || ""));
  // If user clears the value, immediately clear error and AntD state (do not validate empties)
  if (
    newValue === undefined ||
    newValue === null ||
    String(newValue).trim() === ""
  ) {
    phoneFieldErrors.value[key] = "";
    try {
      formRef.value?.clearValidate?.([key]);
    } catch (e) {}
    return;
  }

  if (hadError) {
    validatePhoneField(property);
  }
};

const validateForm = () => {
  return formRef.value
    ?.validateFields()
    .then((values) => {
      fieldErrors.value = {};
      return values;
    })
    .catch((errorInfo) => {
      scrollToFirstError(errorInfo);
      const errors = {};
      (errorInfo.errorFields || []).forEach(({ name, errors: messages }) => {
        const fieldKey = Array.isArray(name) ? name[0] : name;
        errors[fieldKey] = true;
        const property = filteredProperties.value.find(
          (p) => getFieldKey(p) === fieldKey,
        );
        if (property && isPhoneInput(property)) {
          phoneFieldErrors.value[fieldKey] =
            messages?.[0] || `${getFieldLabel(property)} is required!`;
        }
      });
      fieldErrors.value = errors;
      return Promise.reject(errorInfo);
    });
};

const resetForm = () => {
  formRef.value?.resetFields();
  initializeForm();
};

const getFormData = () => {
  const values = { ...formState };

  (filteredProperties.value || []).forEach((property) => {
    const fieldKey = getFieldKey(property);
    if (isPhoneInput(property)) {
      const raw = values[fieldKey];
      if (raw === undefined || raw === null || String(raw).trim() === "") {
        phoneFieldErrors.value[fieldKey] = ""; // don't validate empties in getter
      } else {
        phoneFieldErrors.value[fieldKey] = !isValidPhone(String(raw))
          ? "Please enter a valid phone number"
          : "";
      }
    }
    if (isDateInput(property) && values[fieldKey]) {
      if (dayjs.isDayjs(values[fieldKey])) {
        const fieldType = (
          property.type ||
          property.datatype ||
          ""
        ).toLowerCase();
        values[fieldKey] =
          fieldType === ATTRIBUTE_TYPES.DATETIME
            ? values[fieldKey].format("YYYY-MM-DD HH:mm:ss")
            : values[fieldKey].format("YYYY-MM-DD");
      }
    }
  });

  return values;
};

const getOriginalBaseline = () => {
  return normalizeOriginalDataForTransform(
    originalRecord.value || {},
    props.properties || [],
  );
};

watch(
  () => props.properties,
  async (newProps, oldProps) => {
    if ((newProps || []).length > 0 && props.visible) {
      await fetchFormLayout();
      initializeForm();
    }
  },
  { immediate: true },
);

watch(
  () => props.initialData,
  (newData) => {
    if (props.visible && newData) {
      Object.assign(formState, newData);
    }
  },
  { deep: true },
);

watch(
  () => hasNoProperties.value,
  (isEmpty) => {
    emit("properties-empty", isEmpty);
  },
  { immediate: true },
);

watch(
  () => formState,
  (newState) => {
    (filteredProperties.value || []).forEach((property) => {
      if (isPhoneInput(property)) {
        const fieldKey = getFieldKey(property);
        const hasError = phoneFieldErrors.value[fieldKey];

        if (hasError) {
          validatePhoneField(property);
        }
      }
    });
  },
  { deep: true },
);

watch(
  () => props.visible,
  (newVisible) => {
    if (!newVisible) {
      abortManager.abortAll();
      loadingFields.value = {};
      resetForm();
    }
  },
);

const getFilteredProperties = () => filteredProperties.value;

onMounted(async () => {
  store.dispatch("workspace/fetchDefaultCountryCode");
  emit("form-ready", {
    validateForm,
    resetForm,
    getFormData,
    getOriginalBaseline,
    hasDuplicates: () => hasDuplicates.value,
    getFilteredProperties,
  });
});

defineExpose({
  validateForm,
  resetForm,
  getFormData,
  getOriginalBaseline,
  hasDuplicates,
  getFilteredProperties,
  // Reactive form state — consumers can watch this to react to live edits.
  formState,
});

const getRecordCreateOptions = (property) => {
  if (props.slug === "invoices" && property.slug === "payment_terms") {
    return {
      label: "Create New Payment Term",
      initialData: { is_active: "Yes" },
      fieldTooltips: PAYMENT_TERM_FIELD_TOOLTIPS,
    };
  }
  return null;
};
</script>
